import { useEffect, useRef } from "react";
import "./UserLocationMarker.scss";

interface UserLocationMarkerProps {
  map: google.maps.Map | undefined;
  position: google.maps.LatLngLiteral | null;
}

const UserLocationMarker: React.FC<UserLocationMarkerProps> = ({
  map,
  position,
}) => {
  const markerRef = useRef<google.maps.OverlayView | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map || !position) {
      return;
    }

    class CustomOverlay extends google.maps.OverlayView {
      private div: HTMLDivElement | null = null;
      private currentPosition: google.maps.LatLngLiteral | null = null;

      constructor(initialPosition: google.maps.LatLngLiteral | null) {
        super();
        this.currentPosition = initialPosition;
      }

      onAdd() {
        this.div = document.createElement("div");
        this.div.className = "user-location-dot";
        divRef.current = this.div;

        const panes = this.getPanes();
        panes?.overlayMouseTarget.appendChild(this.div);
      }

      draw() {
        if (this.div && this.currentPosition) {
          const overlayProjection = this.getProjection();
          const sw = overlayProjection.fromLatLngToDivPixel(
            new google.maps.LatLng(
              this.currentPosition.lat,
              this.currentPosition.lng
            )
          );
          if (sw) {
            this.div.style.left = `${sw.x}px`;
            this.div.style.top = `${sw.y}px`;
          }
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          this.div = null;
          divRef.current = null;
        }
      }

      updatePosition(newPosition: google.maps.LatLngLiteral | null) {
        this.currentPosition = newPosition;
        if (this.div && !newPosition) {
          this.div.style.display = "none";
        } else if (this.div && newPosition) {
          this.div.style.display = "";
          this.draw();
        }
      }

      show() {
        if (this.div) {
          this.div.style.display = "";
        }
      }

      hide() {
        if (this.div) {
          this.div.style.display = "none";
        }
      }
    }

    if (markerRef.current) {
      (markerRef.current as CustomOverlay).updatePosition(position);
    } else {
      markerRef.current = new CustomOverlay(position);
      markerRef.current.setMap(map);
    }

    // Initial visibility based on position
    if (position) {
      (markerRef.current as CustomOverlay)?.show();
    } else {
      (markerRef.current as CustomOverlay)?.hide();
    }

    // Cleanup function
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [map, position]);

  return null;
};

export default UserLocationMarker;
